<template>
  <div>
    <div class="container">
      <div class="box-title">
        <h4>
          <i class="fal fa-chart-pie"></i>
          ສະຫຼຸບກະເປົ໋າ (Wallet Monitor)
          <span v-if="data" class="countTotal">{{ tierLabel(data.tier) }}</span>
        </h4>
        <vs-button flat size="small" @click="fetchSummary" :loading="isLoading">
          <i class="fal fa-sync-alt"></i> ໂຫລດໃໝ່
        </vs-button>
      </div>

      <!-- Filters -->
      <div class="box">
        <div class="columns">
          <div class="column is-4">
            <label class="label">ສາຂາ</label>
            <BranchSelect v-model="filters.branch_id" placeholder="ທັງໝົດ" @input="fetchSummary" />
          </div>
          <div class="column is-3">
            <label class="label">ແຕ່ວັນທີ</label>
            <input type="date" class="input" v-model="filters.from_date" />
          </div>
          <div class="column is-3">
            <label class="label">ຫາວັນທີ</label>
            <input type="date" class="input" v-model="filters.to_date" />
          </div>
          <div class="column is-2 apply-col">
            <vs-button @click="fetchSummary" :loading="isLoading"><i class="fal fa-search"></i> ຄົ້ນຫາ</vs-button>
          </div>
        </div>
      </div>

      <!-- Go-unavailable degradation banner (11.4): local SWT figures still render. -->
      <div v-if="data && data.wallet_service_available === false" class="notification is-warning go-banner">
        <i class="fal fa-exclamation-triangle"></i>
        ຍອດເງິນ live ຈາກລະບົບກະເປົ໋າບໍ່ສາມາດເຊື່ອມຕໍ່ໄດ້ — ສະແດງສະເພາະຍອດການເຄື່ອນໄຫວ (SWT) ເທົ່ານັ້ນ.
      </div>

      <!-- Loading / error -->
      <div v-if="isLoading" class="box has-text-centered py-5">
        <i class="fal fa-spinner fa-spin fa-2x"></i><p class="mt-3">ກຳລັງໂຫລດ...</p>
      </div>
      <div v-else-if="error" class="box notification is-warning">
        <p>{{ error }}</p>
        <p v-if="errorRequestId" class="error-ref">ref: {{ errorRequestId }}</p>
        <vs-button @click="fetchSummary" class="mt-3"><i class="fal fa-redo"></i> ລອງໃໝ່</vs-button>
      </div>

      <template v-else-if="data">
        <!-- Wallet totals: FREIGHT and COD are reported separately, never summed. Click → its transactions. -->
        <div class="columns">
          <div class="column is-6">
            <div class="box total-card freight clickable" @click="goToTransactions('1')" title="ກົດເພື່ອເບິ່ງລາຍການ">
              <div class="total-head"><span class="tag is-info">ຄ່າຂົນສົ່ງ</span> ຍອດສຸດທິ <i class="fal fa-search-plus detail-hint"></i></div>
              <div class="total-net">{{ formatCurrency(walletTotals.freight.net) }}</div>
              <div class="total-sub">
                <span>ຮັບ: {{ formatCurrency(walletTotals.freight.charge) }}</span>
                <span>ຄືນ: {{ formatCurrency(walletTotals.freight.refund) }}</span>
                <span>{{ walletTotals.freight.count }} ລາຍການ</span>
              </div>
            </div>
          </div>
          <div class="column is-6">
            <div class="box total-card cod clickable" @click="goToTransactions('2')" title="ກົດເພື່ອເບິ່ງລາຍການ">
              <div class="total-head"><span class="tag is-warning">COD</span> ຍອດສຸດທິ <i class="fal fa-search-plus detail-hint"></i></div>
              <div class="total-net">{{ formatCurrency(walletTotals.cod.net) }}</div>
              <div class="total-sub">
                <span>ຮັບ: {{ formatCurrency(walletTotals.cod.charge) }}</span>
                <span>ຄືນ: {{ formatCurrency(walletTotals.cod.refund) }}</span>
                <span>{{ walletTotals.cod.count }} ລາຍການ</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Spend by pay-type × PreOrder / Direct / Unknown (Unknown shown separately). -->
        <div class="box">
          <h5 class="section-title">ຍອດໃຊ້ຈ່າຍຕາມປະເພດ</h5>
          <table class="table is-fullwidth is-hoverable is-striped">
            <thead>
              <tr><th>ປະເພດ</th><th class="num">{{ preOrderLabel(1) }}</th><th class="num">{{ preOrderLabel(0) }}</th><th class="num">{{ preOrderLabel(null) }}</th><th class="num">ລວມ</th></tr>
            </thead>
            <tbody>
              <tr v-for="row in spendRows" :key="row.key">
                <td>{{ row.label }}</td>
                <td class="num">{{ formatCurrency(row.bucket.pre_order) }}</td>
                <td class="num">{{ formatCurrency(row.bucket.direct) }}</td>
                <td class="num">{{ formatCurrency(row.bucket.unknown) }}</td>
                <td class="num"><strong>{{ formatCurrency(row.bucket.total) }}</strong></td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="columns">
          <!-- Live Go balance overlay (per focused branch) or "unavailable". -->
          <div class="column is-6">
            <div class="box">
              <h5 class="section-title">ຍອດເງິນ live (ລະບົບກະເປົ໋າ)</h5>
              <div v-if="liveWallets && liveWallets.length">
                <div v-for="w in liveWallets" :key="w.wallet_type || w.id" class="live-row">
                  <span class="tag" :class="(w.wallet_type === 'COD') ? 'is-warning' : 'is-info'">{{ walletLabel(w.wallet_type) }}</span>
                  <span class="live-bal">{{ formatCurrency(w.balance) }}</span>
                  <span v-if="w.available != null" class="live-avail">ໃຊ້ໄດ້: {{ formatCurrency(w.available) }}</span>
                </div>
              </div>
              <p v-else class="has-text-grey">{{ liveHint }}</p>
            </div>
          </div>
          <!-- Branch owner. -->
          <div class="column is-6">
            <div class="box">
              <h5 class="section-title">ຂໍ້ມູນສາຂາ</h5>
              <div class="info-row"><span class="info-label">ເຈົ້າຂອງສາຂາ</span>
                <span class="info-value">{{ (data.branch_owner && (data.branch_owner.name || data.branch_owner.owner)) || '—' }}</span>
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import walletMonitorService from '@/services/walletMonitorService';
import { extractErrorInfo } from '@/services/walletService';
import BranchSelect from '@/components/BranchSelect.vue';
import { formatCurrency } from '@/services/walletFormatters';
import { walletLabel, preOrderLabel, normalizeLiveWallets, todayStr } from '@/services/walletMonitorLabels';

const EMPTY_BUCKET = { pre_order: 0, direct: 0, unknown: 0, total: 0 };
const EMPTY_WALLET = { net: 0, charge: 0, refund: 0, count: 0 };

export default {
  name: 'WalletMonitorSummary',
  components: { BranchSelect },
  data() {
    return {
      data: null,
      isLoading: false,
      error: null,
      errorRequestId: null,
      abortController: null,
      filters: { branch_id: '', from_date: todayStr(), to_date: todayStr() },
    };
  },
  computed: {
    walletTotals() {
      const t = (this.data && this.data.wallet_totals) || {};
      return { freight: { ...EMPTY_WALLET, ...(t.freight || {}) }, cod: { ...EMPTY_WALLET, ...(t.cod || {}) } };
    },
    spendRows() {
      const s = (this.data && this.data.spend) || {};
      const rows = [
        { key: 'origin', label: 'ຄ່າຂົນສົ່ງຕົ້ນທາງ', bucket: { ...EMPTY_BUCKET, ...(s.origin || {}) } },
        { key: 'destination', label: 'ຄ່າຂົນສົ່ງປາຍທາງ', bucket: { ...EMPTY_BUCKET, ...(s.destination || {}) } },
        { key: 'cod', label: 'COD', bucket: { ...EMPTY_BUCKET, ...(s.cod || {}) } },
      ];
      // Include the "other" (unrecognised pay-type) bucket so it is never silently dropped; hidden when zero.
      const other = { ...EMPTY_BUCKET, ...(s.other || {}) };
      if (other.total) rows.push({ key: 'other', label: 'ອື່ນໆ', bucket: other });
      return rows;
    },
    liveWallets() {
      const live = this.data && this.data.live;
      return live ? normalizeLiveWallets(live.wallets) : [];
    },
    // Distinguish "Go down" vs "no focused branch" vs "branch selected but no live data" (authoritative payload).
    liveHint() {
      const d = this.data;
      if (!d) return '';
      if (d.wallet_service_available === false) return 'ບໍ່ສາມາດເຊື່ອມຕໍ່ລະບົບກະເປົ໋າ';
      if (d.branch_id == null) return 'ເລືອກສາຂາໃດໜຶ່ງເພື່ອເບິ່ງຍອດ live';
      return 'ບໍ່ມີຂໍ້ມູນຍອດ live';
    },
  },
  // Search-driven: no auto-load on open; the user clicks ຄົ້ນຫາ. The branch picker (BranchSelect)
  // loads its own options on demand via server-side search.
  beforeDestroy() {
    if (this.abortController) this.abortController.abort();
  },
  methods: {
    formatCurrency, walletLabel, preOrderLabel,
    tierLabel(tier) {
      return { admin: '(ຜູ້ດູແລ)', manager: '(ຜູ້ຈັດການສາຂາ)', self: '(ພະນັກງານ)' }[tier] || '';
    },
    // Drill from a total card into the matching transactions, carrying the current scope + date range.
    goToTransactions(walletType) {
      this.$router.push({
        name: 'wallet.monitor.transactions',
        query: {
          wallet_type: walletType,
          branch_id: this.filters.branch_id || undefined,
          from_date: this.filters.from_date || undefined,
          to_date: this.filters.to_date || undefined,
        },
      }).catch(() => {});
    },
    async fetchSummary() {
      if (this.abortController) this.abortController.abort();
      this.abortController = new AbortController();
      this.isLoading = true;
      this.error = null;
      this.errorRequestId = null;
      try {
        const res = await walletMonitorService.getSummary(this.filters);
        this.data = res.data || res;
      } catch (error) {
        if (error.name === 'AbortError' || error.name === 'CanceledError') return;
        const { message, requestId } = extractErrorInfo(error);
        this.error = message || 'ບໍ່ສາມາດໂຫລດຂໍ້ມູນໄດ້';
        this.errorRequestId = requestId;
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import './date-input';
.container { margin-top: 20px; }
.box-title {
  margin-bottom: 20px; display: flex; justify-content: space-between; align-items: center;
  h4 { font-size: 18px; font-weight: 600; i { margin-right: 8px; color: #3273dc; } }
}
.countTotal { color: #666; font-weight: normal; margin-left: 6px; }
.apply-col { display: flex; align-items: flex-end; }
.go-banner i { margin-right: 6px; }
.py-5 { padding-top: 3rem; padding-bottom: 3rem; }
.mt-3 { margin-top: 1rem; }
.error-ref { font-size: 12px; color: #856404; margin-top: 4px; }
.section-title { font-size: 15px; font-weight: 600; margin-bottom: 12px; }
.num { text-align: right; }
.total-card { border-left: 4px solid #3273dc; }
.total-card.cod { border-left-color: #f0ad4e; }
.total-card.clickable { cursor: pointer; transition: box-shadow 0.15s ease, transform 0.15s ease; }
.total-card.clickable:hover { box-shadow: 0 2px 12px rgba(0,0,0,0.12); transform: translateY(-1px); }
.detail-hint { color: #bbb; font-size: 12px; margin-left: 4px; }
.total-head { color: #666; font-size: 14px; margin-bottom: 6px; }
.total-net { font-size: 26px; font-weight: 700; }
.total-sub { margin-top: 6px; display: flex; gap: 16px; font-size: 13px; color: #777; }
.live-row { display: flex; align-items: center; gap: 12px; padding: 6px 0; }
.live-bal { font-weight: 600; }
.live-avail { color: #777; font-size: 13px; }
.info-row { display: flex; justify-content: space-between; margin-bottom: 8px; .info-label { color: #666; } .info-value { font-weight: 500; } }
</style>
